/**
 * Hero Layout Icon Components - Color-Coded Edition with Icons
 * 
 * Dynamically generates icons from preset columnSequences.
 * Each item type is shown with appropriate icon/text:
 * - Image: Image icon in Green (#10B981)
 * - Title: "Title" text in Blue (#3B82F6) - larger font
 * - Text: "Text" text in Purple (#8B5CF6) - smaller font
 * - Button Collection: 3 small buttons in Orange (#F59E0B)
 * - Video: Video icon in Red (#EF4444)
 * - File: File icon in Gray (#6B7280)
 * 
 * Icons show items per column, not column widths.
 * All columns have equal width.
 */

import React from 'react';
import { ITEM_TYPE_COLORS } from './heroLayoutPresets';

/**
 * Icon component for Image item type - Landscape
 */
function ImageLandscapeIcon({ x, y, width, height, color }) {
  const padding = Math.min(width, height) * 0.2;
  // Maintain aspect ratio - landscape is roughly 16:9 or 4:3
  const maxIconW = width - padding * 2;
  const maxIconH = height - padding * 2;
  const aspectRatio = 16 / 9;
  
  let iconW, iconH;
  if (maxIconW / maxIconH > aspectRatio) {
    // Height constrained
    iconH = maxIconH;
    iconW = iconH * aspectRatio;
  } else {
    // Width constrained
    iconW = maxIconW;
    iconH = iconW / aspectRatio;
  }
  
  // Center the icon
  const iconX = x + (width - iconW) / 2;
  const iconY = y + (height - iconH) / 2;
  
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} rx="2" fill={color} opacity="0.15" />
      {/* Landscape frame */}
      <rect x={iconX} y={iconY} width={iconW} height={iconH} rx="1" fill="none" stroke={color} strokeWidth="1" opacity="0.8" />
      {/* Mountain/landscape icon */}
      <path
        d={`M ${iconX} ${iconY + iconH} 
            L ${iconX + iconW * 0.3} ${iconY + iconH * 0.5} 
            L ${iconX + iconW * 0.6} ${iconY + iconH * 0.7} 
            L ${iconX + iconW} ${iconY + iconH * 0.3} 
            L ${iconX + iconW} ${iconY + iconH} Z`}
        fill={color}
        opacity="0.7"
      />
      {/* Sun/circle */}
      <circle
        cx={iconX + iconW * 0.75}
        cy={iconY + iconH * 0.25}
        r={Math.min(iconW, iconH) * 0.12}
        fill={color}
        opacity="0.7"
      />
    </g>
  );
}

/**
 * Icon component for Image item type - Portrait (Profile)
 */
function ImagePortraitIcon({ x, y, width, height, color }) {
  const padding = Math.min(width, height) * 0.2;
  // Maintain aspect ratio - portrait is roughly 2:3
  const maxIconW = width - padding * 2;
  const maxIconH = height - padding * 2;
  const aspectRatio = 2 / 3;
  
  let iconW, iconH;
  if (maxIconW / maxIconH > aspectRatio) {
    // Height constrained
    iconH = maxIconH;
    iconW = iconH * aspectRatio;
  } else {
    // Width constrained
    iconW = maxIconW;
    iconH = iconW / aspectRatio;
  }
  
  // Center the icon
  const iconX = x + (width - iconW) / 2;
  const iconY = y + (height - iconH) / 2;
  
  // Profile icon proportions
  const headRadius = iconW * 0.25;
  const headCenterX = iconX + iconW / 2;
  const headCenterY = iconY + iconH * 0.3;
  
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} rx="2" fill={color} opacity="0.15" />
      {/* Portrait frame */}
      <rect x={iconX} y={iconY} width={iconW} height={iconH} rx="1" fill="none" stroke={color} strokeWidth="1" opacity="0.8" />
      {/* Profile head (filled circle) */}
      <circle
        cx={headCenterX}
        cy={headCenterY}
        r={headRadius}
        fill={color}
        opacity="0.7"
      />
      {/* Profile shoulders/body (filled shape extending to bottom) */}
      <path
        d={`M ${iconX} ${iconY + iconH}
            L ${iconX} ${iconY + iconH * 0.65}
            Q ${iconX + iconW * 0.15} ${iconY + iconH * 0.5}
            ${headCenterX} ${iconY + iconH * 0.48}
            Q ${iconX + iconW * 0.85} ${iconY + iconH * 0.5}
            ${iconX + iconW} ${iconY + iconH * 0.65}
            L ${iconX + iconW} ${iconY + iconH}
            Z`}
        fill={color}
        opacity="0.7"
      />
    </g>
  );
}

/**
 * Icon component for Image item type - chooses landscape or portrait based on dimensions
 */
function ImageIcon({ x, y, width, height, color }) {
  // If height > width, use portrait (profile) icon
  const isPortrait = height > width;
  
  if (isPortrait) {
    return <ImagePortraitIcon x={x} y={y} width={width} height={height} color={color} />;
  } else {
    return <ImageLandscapeIcon x={x} y={y} width={width} height={height} color={color} />;
  }
}

/**
 * Icon component for Video item type
 */
function VideoIcon({ x, y, width, height, color }) {
  const padding = Math.min(width, height) * 0.2;
  // Maintain 16:9 aspect ratio for video
  const maxIconW = width - padding * 2;
  const maxIconH = height - padding * 2;
  const aspectRatio = 16 / 9;
  
  let iconW, iconH;
  if (maxIconW / maxIconH > aspectRatio) {
    // Height constrained
    iconH = maxIconH;
    iconW = iconH * aspectRatio;
  } else {
    // Width constrained
    iconW = maxIconW;
    iconH = iconW / aspectRatio;
  }
  
  // Center the icon
  const iconX = x + (width - iconW) / 2;
  const iconY = y + (height - iconH) / 2;
  
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} rx="2" fill={color} opacity="0.15" />
      {/* Video frame */}
      <rect 
        x={iconX} 
        y={iconY} 
        width={iconW} 
        height={iconH} 
        rx="1" 
        fill="none" 
        stroke={color} 
        strokeWidth="1" 
        opacity="0.8" 
      />
      {/* Play button (triangle) */}
      <path
        d={`M ${iconX + iconW * 0.4} ${iconY + iconH * 0.3} 
            L ${iconX + iconW * 0.4} ${iconY + iconH * 0.7} 
            L ${iconX + iconW * 0.7} ${iconY + iconH * 0.5} Z`}
        fill={color}
        opacity="0.7"
      />
    </g>
  );
}

/**
 * Icon component for File item type
 */
function FileIcon({ x, y, width, height, color }) {
  const padding = Math.min(width, height) * 0.2;
  // Maintain aspect ratio for file (paper is taller than wide)
  const maxIconW = width - padding * 2;
  const maxIconH = height - padding * 2;
  const aspectRatio = 8.5 / 11; // Letter paper ratio
  
  let iconW, iconH;
  if (maxIconW / maxIconH > aspectRatio) {
    // Height constrained
    iconH = maxIconH;
    iconW = iconH * aspectRatio;
  } else {
    // Width constrained
    iconW = maxIconW;
    iconH = iconW / aspectRatio;
  }
  
  // Center the icon
  const iconX = x + (width - iconW) / 2;
  const iconY = y + (height - iconH) / 2;
  
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} rx="2" fill={color} opacity="0.15" />
      {/* File document outline */}
      <path
        d={`M ${iconX} ${iconY} 
            L ${iconX + iconW * 0.7} ${iconY} 
            L ${iconX + iconW} ${iconY + iconH * 0.25} 
            L ${iconX + iconW} ${iconY + iconH} 
            L ${iconX} ${iconY + iconH} Z`}
        fill="none"
        stroke={color}
        strokeWidth="1"
        opacity="0.8"
      />
      {/* Folded corner */}
      <path
        d={`M ${iconX + iconW * 0.7} ${iconY} 
            L ${iconX + iconW * 0.7} ${iconY + iconH * 0.2} 
            L ${iconX + iconW} ${iconY + iconH * 0.25} Z`}
        fill="none"
        stroke={color}
        strokeWidth="1"
        opacity="0.8"
      />
      {/* Document lines */}
      <line
        x1={iconX + iconW * 0.2}
        y1={iconY + iconH * 0.4}
        x2={iconX + iconW * 0.8}
        y2={iconY + iconH * 0.4}
        stroke={color}
        strokeWidth="1"
        opacity="0.6"
      />
      <line
        x1={iconX + iconW * 0.2}
        y1={iconY + iconH * 0.55}
        x2={iconX + iconW * 0.8}
        y2={iconY + iconH * 0.55}
        stroke={color}
        strokeWidth="1"
        opacity="0.6"
      />
      <line
        x1={iconX + iconW * 0.2}
        y1={iconY + iconH * 0.7}
        x2={iconX + iconW * 0.65}
        y2={iconY + iconH * 0.7}
        stroke={color}
        strokeWidth="1"
        opacity="0.6"
      />
    </g>
  );
}

/**
 * Icon component for Button Collection item type
 */
function ButtonIcon({ x, y, width, height, color }) {
  const padding = Math.min(width, height) * 0.25;
  const btnGap = width * 0.08;
  const btnWidth = (width - padding * 2 - btnGap * 2) / 3;
  const btnHeight = height - padding * 2;
  
  // Maintain reasonable aspect ratio for buttons (not too tall/narrow)
  const maxBtnHeight = btnWidth * 1.2;
  const actualBtnHeight = Math.min(btnHeight, maxBtnHeight);
  const btnY = y + padding + (btnHeight - actualBtnHeight) / 2;
  
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} rx="2" fill={color} opacity="0.15" />
      {/* Three small buttons - outline only */}
      {[0, 1, 2].map((i) => (
        <rect
          key={i}
          x={x + padding + i * (btnWidth + btnGap)}
          y={btnY}
          width={btnWidth}
          height={actualBtnHeight}
          rx="1.5"
          fill="none"
          stroke={color}
          strokeWidth="1"
          opacity="0.8"
        />
      ))}
    </g>
  );
}

/**
 * Text component for Title item type
 */
function TitleText({ x, y, width, height, color }) {
  const fontSize = Math.min(width, height) * 0.35;
  const textY = y + height / 2 + fontSize * 0.3;
  
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} rx="2" fill={color} opacity="0.15" />
      <text
        x={x + width / 2}
        y={textY}
        fontSize={fontSize}
        fontWeight="600"
        fill={color}
        textAnchor="middle"
        dominantBaseline="middle"
      >
        Title
      </text>
    </g>
  );
}

/**
 * Text component for Text item type
 */
function TextText({ x, y, width, height, color }) {
  const fontSize = Math.min(width, height) * 0.28;
  const textY = y + height / 2 + fontSize * 0.3;
  
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} rx="2" fill={color} opacity="0.15" />
      <text
        x={x + width / 2}
        y={textY}
        fontSize={fontSize}
        fontWeight="400"
        fill={color}
        textAnchor="middle"
        dominantBaseline="middle"
      >
        Text
      </text>
    </g>
  );
}

/**
 * Get the appropriate icon component for an item type
 */
function getItemIcon(itemType, x, y, width, height, color) {
  switch (itemType) {
    case 'image':
      return <ImageIcon key={`${x}-${y}`} x={x} y={y} width={width} height={height} color={color} />;
    case 'video':
      return <VideoIcon key={`${x}-${y}`} x={x} y={y} width={width} height={height} color={color} />;
    case 'file':
      return <FileIcon key={`${x}-${y}`} x={x} y={y} width={width} height={height} color={color} />;
    case 'button_collection':
      return <ButtonIcon key={`${x}-${y}`} x={x} y={y} width={width} height={height} color={color} />;
    case 'title':
      return <TitleText key={`${x}-${y}`} x={x} y={y} width={width} height={height} color={color} />;
    case 'text':
      return <TextText key={`${x}-${y}`} x={x} y={y} width={width} height={height} color={color} />;
    default:
      return (
        <rect
          key={`${x}-${y}`}
          x={x}
          y={y}
          width={width}
          height={height}
          rx="2"
          fill={color}
          opacity="0.5"
        />
      );
  }
}

/**
 * Generates a color-coded icon showing item types per column
 * @param {Array<Array<string>>} columnSequences - Item types per column
 * @param {number} width - Icon width
 * @param {number} height - Icon height
 */
export function PresetIcon({ columnSequences, width = 96, height = 72 }) {
  if (!columnSequences || columnSequences.length === 0) {
    return <CustomIcon width={width} height={height} />;
  }
  
  const numCols = columnSequences.length;
  const colGap = 4;
  const itemGap = 3;
  const colWidth = (width - (numCols - 1) * colGap) / numCols;
  
  return (
    <svg 
      width={width} 
      height={height} 
      viewBox={`0 0 ${width} ${height}`}
      className="transition-transform group-hover:scale-105"
    >
      {columnSequences.map((items, colIdx) => {
        const x = colIdx * (colWidth + colGap);
        const totalGaps = (items.length - 1) * itemGap;
        const itemHeight = (height - totalGaps) / items.length;
        
        return items.map((itemType, itemIdx) => {
          const y = itemIdx * (itemHeight + itemGap);
          const color = ITEM_TYPE_COLORS[itemType] || '#9CA3AF';
          
          return getItemIcon(itemType, x, y, colWidth, itemHeight, color);
        });
      })}
    </svg>
  );
}

/**
 * Custom layout icon (for custom builder)
 */
export function CustomIcon({ width = 96, height = 72 }) {
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <rect 
        x="10" 
        y="10" 
        width={width - 20} 
        height={height - 20} 
        rx="4" 
        fill="none" 
        stroke="#6B7280" 
        strokeWidth="2" 
        strokeDasharray="4 4" 
      />
      <text 
        x={width / 2} 
        y={height / 2 + 6} 
        fontSize="24" 
        fill="#6B7280" 
        textAnchor="middle"
      >
        +
      </text>
    </svg>
  );
}

// =============================================================================
// Legacy Icon Components (kept for backward compatibility)
// =============================================================================

// Base SVG container with consistent dimensions
const IconContainer = ({ children }) => (
  <svg 
    width="80" 
    height="60" 
    viewBox="0 0 80 60" 
    xmlns="http://www.w3.org/2000/svg"
    className="hero-layout-icon"
  >
    {children}
  </svg>
);

// Column component with proportional widths
const Column = ({ x, width, color = '#94a3b8' }) => (
  <rect
    x={x}
    y="5"
    width={width}
    height="50"
    fill={color}
    stroke="#64748b"
    strokeWidth="1"
    rx="2"
  />
);

// Single Column Icons
export const SingleColumn = () => (
  <IconContainer>
    <Column x="10" width="60" />
  </IconContainer>
);

// Two Column Icons
export const TwoEqual = () => (
  <IconContainer>
    <Column x="5" width="32" />
    <Column x="43" width="32" />
  </IconContainer>
);

export const TwoLeftNarrow = () => (
  <IconContainer>
    <Column x="5" width="20" />
    <Column x="31" width="44" />
  </IconContainer>
);

export const TwoRightNarrow = () => (
  <IconContainer>
    <Column x="5" width="44" />
    <Column x="55" width="20" />
  </IconContainer>
);

export const TwoLeftVeryNarrow = () => (
  <IconContainer>
    <Column x="5" width="10" />
    <Column x="21" width="54" />
  </IconContainer>
);

export const TwoRightVeryNarrow = () => (
  <IconContainer>
    <Column x="5" width="54" />
    <Column x="65" width="10" />
  </IconContainer>
);

// Three Column Icons
export const ThreeEqual = () => (
  <IconContainer>
    <Column x="3" width="20" />
    <Column x="28" width="20" />
    <Column x="53" width="20" />
  </IconContainer>
);

export const ThreeCenterWide = () => (
  <IconContainer>
    <Column x="3" width="10" />
    <Column x="18" width="40" />
    <Column x="63" width="10" />
  </IconContainer>
);

export const ThreeDescending = () => (
  <IconContainer>
    <Column x="3" width="30" />
    <Column x="38" width="20" />
    <Column x="63" width="10" />
  </IconContainer>
);

export const ThreeAscending = () => (
  <IconContainer>
    <Column x="3" width="10" />
    <Column x="18" width="20" />
    <Column x="43" width="30" />
  </IconContainer>
);

// Four Column Icons
export const FourLeftWide = () => (
  <IconContainer>
    <Column x="2" width="30" />
    <Column x="35" width="12" />
    <Column x="50" width="12" />
    <Column x="65" width="12" />
  </IconContainer>
);

export const FourRightWide = () => (
  <IconContainer>
    <Column x="2" width="12" />
    <Column x="17" width="12" />
    <Column x="32" width="12" />
    <Column x="47" width="30" />
  </IconContainer>
);

export const FourCenterWide = () => (
  <IconContainer>
    <Column x="2" width="10" />
    <Column x="15" width="20" />
    <Column x="38" width="20" />
    <Column x="61" width="10" />
  </IconContainer>
);

// Asymmetric Icons
export const AsymThreeNWN = () => (
  <IconContainer>
    <Column x="3" width="12" />
    <Column x="20" width="24" />
    <Column x="49" width="12" />
  </IconContainer>
);

export const AsymThreeWNW = () => (
  <IconContainer>
    <Column x="3" width="24" />
    <Column x="32" width="12" />
    <Column x="49" width="24" />
  </IconContainer>
);

export const AsymFourSplit = () => (
  <IconContainer>
    <Column x="2" width="12" />
    <Column x="17" width="12" />
    <Column x="32" width="20" />
    <Column x="55" width="20" />
  </IconContainer>
);

// Custom Layout Icon (gear/plus icon)
export const CustomLayout = () => (
  <IconContainer>
    <g fill="none" stroke="#94a3b8" strokeWidth="2">
      {/* Grid pattern */}
      <rect x="15" y="15" width="50" height="30" rx="2" />
      <line x1="32.5" y1="15" x2="32.5" y2="45" />
      <line x1="50" y1="15" x2="50" y2="45" />
      {/* Plus sign */}
      <circle cx="40" cy="30" r="10" fill="#f8fafc" stroke="#64748b" strokeWidth="1.5" />
      <line x1="40" y1="25" x2="40" y2="35" stroke="#64748b" strokeWidth="2" />
      <line x1="35" y1="30" x2="45" y2="30" stroke="#64748b" strokeWidth="2" />
    </g>
  </IconContainer>
);

// Icon component mapping
export const ICON_COMPONENTS = {
  SingleColumn,
  TwoEqual,
  TwoLeftNarrow,
  TwoRightNarrow,
  TwoLeftVeryNarrow,
  TwoRightVeryNarrow,
  ThreeEqual,
  ThreeCenterWide,
  ThreeDescending,
  ThreeAscending,
  FourLeftWide,
  FourRightWide,
  FourCenterWide,
  AsymThreeNWN,
  AsymThreeWNW,
  AsymFourSplit,
  CustomLayout
};

/**
 * Get icon component by name
 * @param {string} iconName - Name of the icon component
 * @returns {React.Component} - Icon component
 */
export function getIconComponent(iconName) {
  return ICON_COMPONENTS[iconName] || SingleColumn;
}

